import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  EventDashboardDetails,
  AlertNotice,
  ScrollFade,
  StudentDetailsModal,
  NoResults,
  ModalInfo,
  AlertMessage,
  MarkAsDeniedModal,
} from 'shared/components';
import Parser from 'html-react-parser';
import Pagination from './pagination/Pagination';
import RosterList from './roster-list/RosterList';
import SearchBox from './search-box/SearchBox';
import Filters from './filters/Filters';
import { useTranslation } from 'react-i18next';
import * as roomActions from 'features/room-builder/duck';
import * as taskActions from 'features/task-management/duck';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as seatingChartActions from 'features/seating-chart/duck';

import AccomodationIcon from 'assets/svgs/accomodationIconBlack.svg';
import MismatchIcon from 'assets/svgs/mismatchIcon.svg';
import NotSeatedIcon from 'assets/svgs/notSeated.svg';
import './RoomRoster.scss';
import { difference, isEmpty, isEqual, uniq } from 'lodash';
import RoomRosterService from './RoomRosterService';
import { DEFAULT_PARAMS, pageSizeOptions } from './helpers';

const RoomRoster = () => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { eventId, roomId } = useParams();
  const { t } = useTranslation();

  const assignedStudents = useSelector((state) => state.seatingChart.regSummaries);
  const mappedRoomTiming = useSelector((state) => state.statics.mappedRoomTiming);
  const room = useSelector((state) => state.roomOptions.room);
  const roomStatus = useSelector((state) => state.taskManagement.roomStatus);
  const tcEvent = useSelector((state) => state.tcEvent.currentEvent);
  const testCenterCycle = useSelector((state) => state.tcEvent.testCenterCycle || {});

  const isSmallScreen = window.innerWidth < 1200;

  const [errorMessage, setErrorMessage] = useState('');
  const [filterParams, setFilterParams] = useState({});
  const [markAsAbsentModalIsOpen, setMarkAsAbsentModalIsOpen] = useState(false);
  const [markAsDeniedModalIsOpen, setMarkAsDeniedModalIsOpen] = useState(false);
  const [pageParams, setPageParams] = useState({
    limit: DEFAULT_PARAMS.limit,
    offset: 0,
  });
  const [removeStudentModalIsOpen, setRemoveStudentModalIsOpen] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [successAbsentMessage, setSuccessAbsentMessage] = useState('');
  const [successDeniedMessage, setSuccessDeniedMessage] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const allSelected = useMemo(
    () => students.every((student) => selectedStudents.includes(student.id)),
    [students, selectedStudents]
  );

  const examineeList = useMemo(
    () =>
      students.map((student) => ({ ...student, checked: selectedStudents.includes(student.id) })),
    [selectedStudents, students]
  );

  const filterOptions = useMemo(
    () =>
      [
        {
          available: testCenterCycle?.cycle?.seatingChartRequiredFlag,
          label: 'roomRoster.notSeated',
          id: 'notSeated',
          icon: <NotSeatedIcon />,
          checked: false,
        },
        {
          available: true,
          label: 'roomRoster.mismatchOnly',
          id: 'mismatch',
          icon: <MismatchIcon />,
          checked: false,
        },
        {
          available: true,
          label: 'roomRoster.accomodations',
          id: 'accomRosterFlag',
          icon: <AccomodationIcon />,
          checked: false,
        },
      ]
        .filter(({ available }) => available)
        .map((option) => ({ ...option, checked: !!filterParams[option.id] })),
    [filterParams, testCenterCycle]
  );

  const clearFilters = useCallback(() => {
    setFilterParams({});
  }, []);

  const clearSearch = useCallback(() => {
    setSearchParam('');
    setSearchValue('');
  }, []);

  const clearAllParams = useCallback(() => {
    clearFilters();
    clearSearch();
  }, [clearFilters, clearSearch]);

  const closeAbsentModal = useCallback(() => setMarkAsAbsentModalIsOpen(false), []);

  const closeDeniedModal = useCallback(() => setMarkAsDeniedModalIsOpen(false), []);

  const closeRemoveModal = useCallback(() => setRemoveStudentModalIsOpen(false), []);

  const closeStudentDetailsModal = useCallback(() => {
    setSelectedStudent(null);
  }, []);

  const deselectAllStudents = useCallback(() => {
    setSelectedStudents((prev) =>
      difference(
        prev,
        students.map((student) => student.id)
      )
    );
  }, [students]);

  const fetchStudents = useCallback(async (room, filters, search, page) => {
    setStudentsLoading(true);
    const response = await RoomRosterService.getStudentsList(room, filters, search, page);
    setStudents(response.data);
    setTotalCount(response.meta.totalCount);
    setStudentsLoading(false);
  }, []);

  const handlePageChanges = useCallback(
    (startIndex, _endIndex, pageSize) => {
      const params = {
        limit: pageSize,
        offset: startIndex,
      };
      if (isEqual(pageParams, params)) return;
      setPageParams(params);
    },
    [pageParams]
  );

  const handleSearchValueChange = useCallback((e) => {
    const { value } = e.target;
    setSearchValue(value);
  }, []);

  const handleSearch = useCallback(
    (e) => {
      if (!searchValue || (e.key !== 'Enter' && e.type !== 'click')) return;

      setSearchParam(searchValue);
    },
    [searchValue]
  );

  const handleUnassign = useCallback(
    async (absent, deniedReason, deniedReasonDetails) => {
      const data = {
        absent,
        deniedReason: deniedReason || null,
        deniedReasonDetails: deniedReasonDetails || null,
        regIds: selectedStudents,
      };
      try {
        const successCount = await RoomRosterService.unassignExaminees(roomId, data);
        dispatch(seatingChartActions.getRegSummaries(roomId));
        setSelectedStudents([]);
        await fetchStudents(roomId, filterParams, searchValue);
        if (deniedReason) {
          setSuccessDeniedMessage(
            Parser(t('roomRoster.successDeniedMessage', { number: successCount }))
          );
        } else if (absent) {
          setSuccessAbsentMessage(
            Parser(t('roomRoster.successAbsentMessage', { number: successCount }))
          );
        }
      } catch {
        setErrorMessage(t('errors.somethingWentWrong'));
      } finally {
        if (deniedReason) {
          closeDeniedModal();
        } else if (absent) {
          closeAbsentModal();
        } else {
          closeRemoveModal();
        }
      }
    },
    [
      closeAbsentModal,
      closeDeniedModal,
      closeRemoveModal,
      dispatch,
      fetchStudents,
      filterParams,
      roomId,
      searchValue,
      selectedStudents,
      t,
    ]
  );

  const openAbsentModal = useCallback(() => setMarkAsAbsentModalIsOpen(true), []);

  const openDeniedModal = useCallback(() => setMarkAsDeniedModalIsOpen(true), []);

  const openRemoveModal = useCallback(() => setRemoveStudentModalIsOpen(true), []);

  const openStudentDetailsModal = useCallback(setSelectedStudent, [setSelectedStudent]);

  const selectAllStudents = useCallback(() => {
    if (allSelected) return deselectAllStudents();
    setSelectedStudents((prev) => uniq([...prev, ...students.map((student) => student.id)]));
  }, [allSelected, deselectAllStudents, students]);

  const selectFilter = useCallback((e) => {
    const { checked, value } = e.target;
    setFilterParams((prev) => ({
      ...prev,
      [value]: checked ? 1 : undefined,
    }));
  }, []);

  const selectStudent = useCallback(
    (e) => {
      const { value } = e.target;
      setSelectedStudents((prev) =>
        prev.includes(Number(value))
          ? prev.filter((id) => id !== Number(value))
          : [...prev, Number(value)]
      );
    },
    [setSelectedStudents]
  );

  useEffect(() => {
    dispatch(seatingChartActions.getRegSummaries(roomId));
    dispatch(roomActions.getRoom(roomId));
    dispatch(taskActions.getRoomStatus(roomId));
  }, [dispatch, roomId]);

  useEffect(() => {
    dispatch(tcEventActions.getTcEvent(eventId));
    dispatch(tcEventActions.getTestCenterCycle(eventId));
  }, [dispatch, eventId]);

  useEffect(() => {
    if (new URLSearchParams(search).get('redirected') !== 'fromDashboard') return;
    setFilterParams({
      mismatch: 1,
    });
  }, [search]);

  useEffect(() => {
    if (!roomId) return;

    fetchStudents(roomId, filterParams, searchParam, pageParams);
  }, [fetchStudents, filterParams, pageParams, roomId, searchParam]);

  return (
    <div className="room-roster-container">
      <div className="room-roster-header">
        <h3>{t('roomRoster.title')}</h3>
        <EventDashboardDetails
          date={isEmpty(tcEvent) ? '' : tcEvent.testingEvent.name}
          name={roomStatus.testCenterName}>
          <span>{roomStatus.locationName}</span>
          {roomStatus.locationArea && roomStatus.locationArea.length > 0 && (
            <span>, {roomStatus.locationArea}</span>
          )}
        </EventDashboardDetails>

        <p>{room.name}</p>
      </div>
      <div className="labels-container">
        <span>
          {room.roomType ? room.roomType.name : t('testCenter.roomDashboard.noTypeLabel')}
        </span>
        {room.timing ? <span>{mappedRoomTiming[room.timing]?.name}</span> : null}
        {room.roomType && (
          <span>
            {room.roomType.onlineFlag ? t('common.labels.online') : t('common.labels.paper')}
          </span>
        )}
      </div>
      {students.some(
        (item) =>
          item.timingIncompatibility ||
          item.roomTypeIncompatibility ||
          item.singleRoomIncompatibility
      ) && <AlertNotice message={t('roomRoster.examineeMismatch')} type="warning" />}
      <div>
        <div className="search-container">
          <SearchBox
            toggleFilters={setShowFilters}
            showFilters={showFilters}
            className=""
            searchValue={searchValue}
            onChange={handleSearchValueChange}
            onSearch={handleSearch}
            onKeyPress={handleSearch}
            clearSearch={clearSearch}
          />
        </div>
        {isSmallScreen && (
          <ScrollFade>
            <Filters
              setShowFilters={setShowFilters}
              filterOptions={filterOptions}
              selectFilter={selectFilter}
              clearFilters={clearFilters}
              isMobile={isSmallScreen}
            />
          </ScrollFade>
        )}
        <div className="action-bar">
          <Pagination
            itemsTotalCount={totalCount}
            onChangePage={handlePageChanges}
            paginationDataParams={filterParams}
            pageSize={pageParams.limit}
            pageSizeOptions={pageSizeOptions}
            ofLabel={t('rosters.examineesOfLabel')}
            showSelectPage
            setTemporarySearchValue={setSearchValue}
            temporarySearchValue={searchValue}
            listOfSelectedExaminees={selectedStudents}
            openRemoveModal={openRemoveModal}
            examineeList={examineeList}
            onSelectAll={selectAllStudents}
            temporarySelectedExaminees={selectedStudents}
            onDeselectAllHandle={deselectAllStudents}
            openAbsentModal={openAbsentModal}
            openDeniedModal={openDeniedModal}
          />
        </div>
        <div className={`roster-container ${!showFilters && 'no-filters'}`}>
          {showFilters && !isSmallScreen && (
            <Filters
              setShowFilters={setShowFilters}
              filterOptions={filterOptions}
              selectFilter={selectFilter}
              clearFilters={clearFilters}
            />
          )}
          {students.length ? (
            isSmallScreen ? (
              <ScrollFade>
                <RosterList
                  examineeList={examineeList}
                  selectAll={selectAllStudents}
                  allSelected={allSelected}
                  selectExaminee={selectStudent}
                  showStudent={openStudentDetailsModal}
                  room={room}
                  noResultsClear={clearAllParams}
                  countInfo={totalCount}
                  studentsLoading={studentsLoading}
                  assignedStudents={assignedStudents}
                  testCenterCycle={testCenterCycle}
                />
              </ScrollFade>
            ) : (
              <RosterList
                examineeList={examineeList}
                selectAll={selectAllStudents}
                allSelected={allSelected}
                selectExaminee={selectStudent}
                showStudent={openStudentDetailsModal}
                room={room}
                noResultsClear={clearAllParams}
                countInfo={totalCount}
                studentsLoading={studentsLoading}
                assignedStudents={assignedStudents}
                testCenterCycle={testCenterCycle}
              />
            )
          ) : (
            <NoResults noResultsClear={clearAllParams} />
          )}
        </div>
        <button className="btn btn-outline roster-wide-btn" onClick={() => navigate(-1)}>
          {t('common.labels.back')}
        </button>
      </div>
      <StudentDetailsModal
        student={selectedStudent}
        modalIsOpen={!!selectedStudent}
        closeModal={closeStudentDetailsModal}
        nowManagedFlag={testCenterCycle.testCenterProgram?.testCenter?.nowManagedFlag}
      />
      <ModalInfo
        containerClass="remove-student-modal-container"
        onSubmit={() => handleUnassign(false)}
        modalIsOpen={removeStudentModalIsOpen}
        onCancel={closeRemoveModal}
        className="remove-student-modal"
        saveLabel="Remove from room"
        cancelLabel="Cancel"
        showCancel>
        <h5>{t('roomRoster.removeFromRoom')}</h5>
        <div>
          {Parser(
            t(
              testCenterCycle.testCenterProgram?.program.parentCode === 'workkeys'
                ? 'roomRoster.onceRemovedWK'
                : 'roomRoster.onceRemoved'
            )
          )}
        </div>
      </ModalInfo>
      <MarkAsDeniedModal
        modalIsOpen={markAsDeniedModalIsOpen}
        closeModal={closeDeniedModal}
        numberOfSelectedExaminees={selectedStudents.length}
        onSubmit={handleUnassign}
      />
      <ModalInfo
        containerClass="mark-absent-modal-container"
        onSubmit={() => handleUnassign(true)}
        modalIsOpen={markAsAbsentModalIsOpen}
        onCancel={closeAbsentModal}
        className="mark-student-absent-modal"
        saveLabel={t('roomRoster.markAsAbsent')}
        cancelLabel="Cancel"
        showCancel>
        <h5>{t('roomRoster.markAsAbsentQuestion')}</h5>
        <div>
          {Parser(
            t(
              testCenterCycle.testCenterProgram?.program.parentCode === 'workkeys'
                ? 'roomRoster.markAsAbsentTextWK'
                : 'roomRoster.markAsAbsentText',
              { number: selectedStudents.length }
            )
          )}
        </div>
      </ModalInfo>
      <AlertMessage
        show={Boolean(successAbsentMessage)}
        customMessage={successAbsentMessage}
        removeMessage={() => setSuccessAbsentMessage(false)}
      />
      <AlertMessage
        show={Boolean(successDeniedMessage)}
        customMessage={successDeniedMessage}
        removeMessage={() => setSuccessDeniedMessage(false)}
      />
      <AlertMessage
        show={Boolean(errorMessage)}
        message={errorMessage}
        removeMessage={() => setErrorMessage(false)}
        isError
      />
    </div>
  );
};

export default RoomRoster;
